import { useState } from "react";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const ExtractModal = ({
  isMobile,
  cutHeight,
  setCutHeight,
  extractColor,
  setExtractColor,
  extractedName,
  setExtractedName,
  modelRef,
  selectedModels,
  setSelectedModels,
  setProcessing,
  applyExtract,
  closeExtractModal,
}) => {
  const colorInputId = "extractColorInput";
  const [searchTerm, setSearchTerm] = useState("");

  // Check if mobile portrait (upside down) view
  const isMobilePortrait =
    isMobile &&
    typeof window !== "undefined" &&
    window.innerWidth < window.innerHeight;

  // Filter models based on search term
  const allModels = modelRef.current?.children || [];
  const filteredModels = allModels.filter((model) =>
    (model.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      className={`absolute ${
        isMobilePortrait ? "bottom-20 left-2" : "bottom-1 left-28"
      } w-auto md:w-1/4 flex flex-col items-start z-50`}
    >
      <div
        className="relative flex flex-col items-start space-y-3 p-3 pt-2 bg-white rounded-lg shadow-lg border border-gray-200 w-44 md:w-56"
        style={{
          "--main-color": anatomyConstants.mainColor,
          "--main-color-bold": anatomyConstants.mainColorBold,
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            closeExtractModal?.();
          }}
          aria-label="Close"
          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gray-100 border border-[#0000001A] shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-200 z-10"
          title="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Search bar */}
        <div className="w-full">
          <style jsx>{`
            .no-spinner::-webkit-inner-spin-button,
            .no-spinner::-webkit-outer-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            .no-spinner {
              -moz-appearance: textfield;
            }
          `}</style>
          <label className="block text-xs text-gray-600 mb-1">
            Search Models
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="py-1 px-2 w-full bg-white border border-gray-300 rounded text-sm text-[var(--main-color)] focus:outline-none focus:ring-1 focus:ring-[var(--main-color)]"
            placeholder="Filter models"
          />
        </div>

        <div className="w-full">
          {/* Name + Color inline */}
          <label className="block text-xs text-gray-600 mb-1">Name</label>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
            <input
              type="text"
              value={extractedName ?? ""}
              onChange={(e) => setExtractedName?.(e.target.value)}
              className="py-1 px-2 w-full bg-white border border-gray-300 rounded text-sm text-[var(--main-color)] focus:outline-none focus:ring-1 focus:ring-[var(--main-color)]"
              placeholder="Extracted mesh name"
            />

            {/* Hidden color input + visible circular swatch */}
            <input
              id={colorInputId}
              type="color"
              value={extractColor}
              onChange={(e) => setExtractColor(e.target.value)}
              className="sr-only"
              aria-label="Pick extracted color"
            />
            <label
              htmlFor={colorInputId}
              title="Pick color"
              className="w-7 h-7 rounded-full border border-gray-300 cursor-pointer shadow-sm"
              style={{ backgroundColor: extractColor }}
            />
          </div>
        </div>

        <div className="w-full">
          <label className="block text-xs text-gray-600 mb-1">
            Thickness (mm)
          </label>
          <input
            type="number"
            value={Number.isFinite(cutHeight) ? cutHeight : ""}
            onChange={(e) => setCutHeight(parseFloat(e.target.value))}
            className="no-spinner py-1 px-2 w-full bg-white border border-gray-300 rounded text-sm text-[var(--main-color)] focus:outline-none focus:ring-1 focus:ring-[var(--main-color)]"
            placeholder="Height"
          />
        </div>

        {/* Models list — reduced height */}
        <div
          className={`w-full flex flex-col space-y-2 overflow-y-auto overflow-x-hidden ${
            isMobile ? "max-h-20" : "max-h-28"
          }`}
        >
          {filteredModels.map((model) => {
            // Get the original index from modelRef.current.children
            const originalIndex = allModels.indexOf(model);
            return (
              <label
                key={model.name || originalIndex}
                className="flex items-center space-x-2 w-full"
              >
                <input
                  className="accent-[var(--main-color)]"
                  type="checkbox"
                  checked={selectedModels.includes(originalIndex)}
                  onChange={() => {
                    setSelectedModels((prev) =>
                      prev.includes(originalIndex)
                        ? prev.filter((i) => i !== originalIndex)
                        : [...prev, originalIndex]
                    );
                  }}
                />
                <span className="text-sm md:text-xs text-gray-700 truncate">
                  {model.name || `Mesh ${originalIndex}`}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex w-full justify-between items-center space-x-2">
          <button
            title="Extracts the selected area into a new mesh (merged if multiple sources)."
            onClick={(e) => {
              e.stopPropagation();
              setProcessing?.(true);
              setTimeout(() => {
                applyExtract?.();
              }, 80);
            }}
            className="flex-1 py-2 px-3 text-sm md:text-xs font-medium text-white rounded bg-[var(--main-color)] hover:bg-[var(--main-color-bold)]"
          >
            Extract
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExtractModal;
